import type Phaser from 'phaser';
import { TILE_SIZE } from '../layout';

/** The crowns sheet: one big tree crown per frame, five tiles across. */
export const CROWNS_KEY = 'crowns';
export const CROWN_SIZE = TILE_SIZE * 5;
/** Crown frames, left to right (append only: art sheets follow this layout). */
export const CROWN_FRAMES = ['oak'] as const;
export type CrownFrame = (typeof CROWN_FRAMES)[number];

/**
 * Leaf clusters of the oak's crown, back (top of the frame) to front: x, y, radius (pixels in the
 * 80 x 80 frame). Front clusters overlap the ones behind them.
 */
const OAK_LOBES: readonly [number, number, number][] = [
  [40, 15, 13],
  [24, 22, 13],
  [56, 21, 14],
  [40, 31, 16],
  [15, 37, 12],
  [65, 37, 12],
  [27, 44, 15],
  [53, 45, 15],
  [40, 56, 15],
  [20, 57, 11],
  [60, 58, 11],
];

// The props' leaf greens (props.ts), deepest to brightest, and the outline ink.
const INK = '#10180c';
const LEAF = ['#123b1e', '#1f5f2b', '#2f8236', '#4ea83f', '#86cf52', '#cdf07a'] as const;

/** Small deterministic hash for leaf texture (textures stay stable). */
function noise(x: number, y: number, salt: number): number {
  let h = Math.imul(x * 374761393 + y * 668265263 + salt * 2147483647, 1274126177);
  h = Math.imul(h ^ (h >>> 13), 1103515245);
  return (h ^ (h >>> 16)) >>> 0;
}

/**
 * A crown seen from above at an angle, lit from the top left: round leaf clusters painted back to
 * front, each cel shaded (bright on its sunny shoulder, deep on its far side), a dark rim where a
 * cluster lies over the one behind, scalloped leaf tufts along the lit edges, and a crisp ink
 * outline round the whole crown.
 */
function paintCrown(
  ctx: CanvasRenderingContext2D,
  ox: number,
  lobes: readonly (readonly [number, number, number])[],
  N = CROWN_SIZE,
  blossom = false,
): void {
  const color = new Array<string | undefined>(N * N);
  const owner = new Int16Array(N * N).fill(-1);
  lobes.forEach(([cx, cy, r], k) => {
    for (let y = Math.max(0, Math.floor(cy - r)); y < Math.min(N, cy + r + 1); y++) {
      for (let x = Math.max(0, Math.floor(cx - r)); x < Math.min(N, cx + r + 1); x++) {
        const ex = (x + 0.5 - cx) / r;
        const ey = (y + 0.5 - cy) / r;
        const d = Math.sqrt(ex * ex + ey * ey);
        // Leafy edge: the rim wobbles a pixel in and out.
        const rim = 1 - (noise(Math.round(Math.atan2(ey, ex) * 6), k, 5) % 3) * 0.035;
        if (d > rim) continue;
        const lit = -(ex * 0.6 + ey * 0.8); // toward the sun: 1, away: -1
        const n = noise(x, y, 31 + k) % 8;
        let c: string;
        if (d > rim - 0.12 && lit < -0.1) c = LEAF[1];
        else if (lit > 0.62) c = n < 3 ? LEAF[5] : LEAF[4];
        else if (lit > 0.25) c = n === 0 ? LEAF[5] : LEAF[4];
        else if (lit > -0.2) c = n === 0 ? LEAF[4] : LEAF[3];
        else if (lit > -0.6) c = n === 0 ? LEAF[3] : LEAF[2];
        else c = n === 0 ? LEAF[2] : LEAF[1];
        const i = y * N + x;
        // Where this cluster's edge lies over one behind it, a dark rim shows the overlap.
        if (owner[i]! >= 0 && d > rim - 0.1) c = d > rim - 0.05 ? INK : LEAF[0];
        color[i] = c;
        owner[i] = k;
      }
    }
  });
  // The underside: the crown's lowest rows fall into deep shade.
  for (let x = 0; x < N; x++) {
    let bottom = -1;
    for (let y = N - 1; y >= 0; y--) {
      if (color[y * N + x]) {
        bottom = y;
        break;
      }
    }
    for (let y = bottom; y > bottom - 3 && y >= 0; y--) {
      if (color[y * N + x] && color[y * N + x] !== INK)
        color[y * N + x] = y === bottom ? LEAF[0] : LEAF[1];
    }
  }
  // A tree in flower: pale blossoms dotted over its sunlit leaves.
  if (blossom) {
    for (let i = 0; i < N * N; i++) {
      const c = color[i];
      if (
        (c === LEAF[4] || c === LEAF[5] || c === LEAF[3]) &&
        noise(i % N, Math.floor(i / N), 77) % 23 === 0
      ) {
        color[i] = noise(i % N, Math.floor(i / N), 78) % 3 === 0 ? '#ffd6e6' : '#fffaf0';
      }
    }
  }
  const solid = (x: number, y: number) => x >= 0 && y >= 0 && x < N && y < N && !!color[y * N + x];
  for (let y = 0; y < N; y++) {
    for (let x = 0; x < N; x++) {
      let c = color[y * N + x];
      if (!c && (solid(x - 1, y) || solid(x + 1, y) || solid(x, y - 1) || solid(x, y + 1))) c = INK;
      if (!c) continue;
      ctx.fillStyle = c;
      ctx.fillRect(ox + x, y, 1, 1);
    }
  }
}

/**
 * The Meadows' grand trees: crowns seven tiles across, fuller and in flower (their own sheet; the
 * trunk tile and the lawn they shade are a big tree's).
 */
export const GRAND_CROWNS_KEY = 'grandCrowns';
export const GRAND_CROWN_SIZE = TILE_SIZE * 7;
export const GRAND_CROWN_FRAMES = ['grandOak'] as const;
const GRAND_SCALE = GRAND_CROWN_SIZE / CROWN_SIZE;
const GRAND_LOBES: readonly (readonly [number, number, number])[] = [
  ...OAK_LOBES.map(([x, y, r]): readonly [number, number, number] => [
    x * GRAND_SCALE,
    y * GRAND_SCALE,
    r * GRAND_SCALE,
  ]),
  [22, 30, 15],
  [90, 32, 14],
];

export function createGrandCrowns(scene: Phaser.Scene): void {
  const N = GRAND_CROWN_SIZE;
  const tex = scene.textures.createCanvas(GRAND_CROWNS_KEY, N * GRAND_CROWN_FRAMES.length, N);
  if (!tex) throw new Error('Could not create grand crowns texture');
  paintCrown(tex.context, 0, GRAND_LOBES, N, true);
  tex.refresh();
  for (let i = 0; i < GRAND_CROWN_FRAMES.length; i++) tex.add(i, 0, i * N, 0, N, N);
}

export function createCrowns(scene: Phaser.Scene): void {
  const tex = scene.textures.createCanvas(CROWNS_KEY, CROWN_SIZE * CROWN_FRAMES.length, CROWN_SIZE);
  if (!tex) throw new Error('Could not create crowns texture');
  paintCrown(tex.context, 0, OAK_LOBES);
  tex.refresh();
  for (let i = 0; i < CROWN_FRAMES.length; i++) {
    tex.add(i, 0, i * CROWN_SIZE, 0, CROWN_SIZE, CROWN_SIZE);
  }
}
